
import { Component, Vue, Watch } from "vue-property-decorator"
import { State, Getter } from "vuex-class"

import { CurrentUser, SystemUser } from "@/types/user"
import { BaseState } from "@/store"
import api from "@/services/api"
import UserMenu from "./UserMenu.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import NotificationsMenu from "@/components/New/Page/Notifications/NotificationsMenu.vue"
import GlobalSearch from "@/components/New/Page/GlobalSearch.vue"

import { Breadcrumb } from "@/types/breadcrumb"

@Component({
	components: {
		NotificationsMenu,
		GlobalSearch,
		"notifications-button": NotificationsMenu,
		"user-menu": UserMenu
	}
})
export default class HeaderBar extends Vue {
	@Getter isFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getIsUserInitComplete!: boolean

	@State((state: BaseState) => state.me) private readonly me!: CurrentUser;

	public systemUser!: SystemUser | null

	public breadcrumbItems: Breadcrumb[] = []

	private pictureError: boolean = false;
	private globalSearchOpen: boolean = false
	private globalSearchHover: boolean = false

	private async mounted (): Promise<void> {
		await this.updateBreadcrumb()
	}

	/**
	 * @summary Gets a value indicating whether the header bar has its content slot occupied.
	 */
	private get hasHeaderContent (): boolean {
		return !!this.$slots["content"]
	}

	/**
	 * @summary Gets a value indicating whether the header bar has its content slot occupied.
	 */
	private get hasSubHeaderContent (): boolean {
		return !!this.$slots["sub-header"]
	}

	// TODO figure out this on system page. Will be in later phase
	private get myPictureUrl (): string {
		let imageUrl: string

		if (this.pictureError || (!this.me && !this.systemUser)) {
			imageUrl = "images/blank_user.png"
		} else {
			if (this.systemUser) {
				imageUrl = this.systemUser.pictureUrl ? this.systemUser.pictureUrl : "images/blank_user.png"
			} else {
				if (this.me?.pictureUrl) {
					imageUrl = this.me.pictureUrl!
				} else {
					imageUrl = api.pictureUrl(this.me?.id)
				}
			}
		}

		return imageUrl
	}

	/**
	 * Only show new interface switch if the features are enabled
	 * @private
	 */
	private get showNewInterfaceSwitch (): boolean {
		return (this.me?.tenantEnabledFeatures?.includes(TenantFeatures.EnableV2) && (this.me?.isAdmin || this.me?.tenantEnabledFeatures?.includes(TenantFeatures.NewUIOptIn))) ?? false
	}

	/**
	 * @summary Updates the breadcrumbs to be displayed in the header bar:
	 * Uses the route's breadcrumb metadata, breadcrumb transform metadata, or the path.
	 */
	@Watch("$route.path")
	private async updateBreadcrumb (): Promise<void> {
		let breadcrumbArray: Breadcrumb[] = []
		let pathArray = this.$route.path.split("/")

		pathArray.shift()

		for (let i = 0; i < pathArray.length; i++) {
			const path = pathArray[i]
			const route = this.$route.matched[i]

			if (!route) {
				break
			}

			const matchedRouteName = route.name as string | undefined
			const resolvedRoute = matchedRouteName
				? this.$router.resolve({
					name: matchedRouteName,
					params: this.$route.params,
					query: this.$route.query
				}).route
				: null

			const resolvedPath = resolvedRoute
				? resolvedRoute.path
				: breadcrumbArray[i - 1]
					? `/${breadcrumbArray[i - 1].path}/${path}`.replace(/\/+/g, "/")
					: `/${path}`

			const isCurrentPage = !!resolvedRoute?.name && resolvedRoute.name === this.$route.name

			const newItem = {
				path: resolvedPath.replace(/^\//, ""),
				to: isCurrentPage ? undefined : resolvedPath,
				active: isCurrentPage,
				text: this.$route.matched[i]?.meta?.breadcrumb || path
			}

			// If the route has a breadcrumb transform defined, call it.
			const breadcrumbTransform = this.$route.matched[i]?.meta?.breadcrumbTransform
			if (breadcrumbTransform && typeof (this.$route.matched[i].meta.breadcrumbTransform) === "function") {
				// As the breadcrumb transform could be an async function or a sync function, we always want to await it
				const result = await breadcrumbTransform(this.$route, newItem, this.$store)

				if (Array.isArray(result)) {
					// Transforms can return multiple breadcrumbs (e.g. for folders).
					breadcrumbArray.push(...result)
				} else {
					breadcrumbArray.push(result)
				}
			} else {
				// Otherwise, just push it to our array.
				breadcrumbArray.push(newItem)
			}
		}

		if (breadcrumbArray[0] !== undefined) {
			const caseNames = breadcrumbArray[0].text.split("&")
			if (caseNames.length > 1) {
				if (caseNames[0].trim() === caseNames[1].trim()) {
					breadcrumbArray[0].text = caseNames[0].trim()
				}
			}
		}
		this.breadcrumbItems = breadcrumbArray
	}
}
